import { supabase } from '../lib/supabase';

/**
 * Bail Agent Service - Handles all bail agent related operations
 */
export const bailAgentService = {
  // Get all bail agents for scheduling (bypasses RLS public restriction)
  async getAllAgentsForScheduling() {
    try {
      // Use .eq() instead of filtering to ensure we bypass public RLS policy
      // This will fetch ALL agents regardless of shift status
      const { data, error } = await supabase
        ?.from('bail_agents')
        ?.select(`
          id,
          full_name,
          license_number,
          phone,
          bio,
          years_experience,
          specializations,
          profile_photo_url,
          is_currently_on_shift,
          status,
          shift_started_at,
          created_at,
          updated_at
        `)
        ?.order('years_experience', { ascending: false }); // Order by experience - fetch ALL agents for scheduling

      if (error) {
        console.error('❌ Supabase fetch error:', error?.message);
        throw new Error(`Failed to fetch agents: ${error?.message}`);
      }

      console.log('DATA FETCHED FROM SUPABASE:', data);
      console.log(`📊 Total agents in database: ${data?.length || 0}`);

      return { data: data || [], error: null };
    } catch (error) {
      console.error('❌ Service error:', error);
      return { 
        data: null, 
        error: error?.message || 'Failed to fetch bail agents'
      };
    }
  },

  // Get all bail agents
  async getAllAgents() {
    try {
      const { data, error } = await supabase?.from('bail_agents')?.select(`
          id,
          full_name,
          license_number,
          phone,
          bio,
          years_experience,
          specializations,
          profile_photo_url,
          is_currently_on_shift,
          status,
          shift_started_at,
          created_at,
          updated_at
        `)?.order('created_at', { ascending: false });

      if (error) {
        throw new Error(`Failed to fetch agents: ${error?.message}`);
      }

      return { data: data || [], error: null };
    } catch (error) {
      return { 
        data: null, 
        error: error?.message || 'Failed to fetch bail agents'
      };
    }
  },

  // Get current agent on shift
  async getCurrentAgent() {
    try {
      const { data, error } = await supabase?.rpc('get_current_agent');

      if (error) {
        throw new Error(`Failed to fetch current agent: ${error?.message}`);
      }

      return { data: data?.[0] || null, error: null };
    } catch (error) {
      return { 
        data: null, 
        error: error?.message || 'Failed to fetch current agent'
      };
    }
  },

  // Get agent by ID
  async getAgentById(agentId) {
    try {
      if (!agentId) {
        throw new Error('Agent ID is required');
      }

      const { data, error } = await supabase?.from('bail_agents')?.select(`
          id,
          full_name,
          license_number,
          phone,
          bio,
          years_experience,
          specializations,
          profile_photo_url,
          is_currently_on_shift,
          status,
          shift_started_at,
          created_at,
          updated_at
        `)?.eq('id', agentId)?.single();

      if (error) {
        throw new Error(`Failed to fetch agent: ${error?.message}`);
      }

      return { data: data || null, error: null };
    } catch (error) {
      return { 
        data: null, 
        error: error?.message || 'Failed to fetch agent details'
      };
    }
  },

  // Create new bail agent
  async createAgent(agentData) {
    try {
      if (!agentData?.full_name || !agentData?.license_number || !agentData?.phone) {
        throw new Error('Name, license number, and phone are required');
      }

      const { data, error } = await supabase?.from('bail_agents')?.insert([{
          full_name: agentData?.full_name,
          license_number: agentData?.license_number,
          phone: agentData?.phone,
          bio: agentData?.bio || '',
          years_experience: parseInt(agentData?.years_experience) || 0,
          specializations: agentData?.specializations || [],
          profile_photo_url: agentData?.profile_photo_url || null,
          status: agentData?.status || 'available'
        }])?.select()?.single();

      if (error) {
        throw new Error(`Failed to create agent: ${error?.message}`);
      }

      return { data, error: null };
    } catch (error) {
      return { 
        data: null, 
        error: error?.message || 'Failed to create bail agent'
      };
    }
  },

  // Update bail agent
  async updateAgent(agentId, agentData) {
    try {
      if (!agentId) {
        throw new Error('Agent ID is required');
      }

      const updateData = {
        ...(agentData?.full_name && { full_name: agentData?.full_name }),
        ...(agentData?.license_number && { license_number: agentData?.license_number }),
        ...(agentData?.phone && { phone: agentData?.phone }),
        bio: agentData?.bio || '',
        years_experience: parseInt(agentData?.years_experience) || 0,
        specializations: agentData?.specializations || [],
        ...(agentData?.profile_photo_url !== undefined && { profile_photo_url: agentData?.profile_photo_url }),
        ...(agentData?.status && { status: agentData?.status }),
        updated_at: new Date()?.toISOString()
      };

      // Enhanced error handling with more specific error messages
      const { data, error } = await supabase
        ?.from('bail_agents')
        ?.update(updateData)
        ?.eq('id', agentId)
        ?.select(`
          id,
          full_name,
          license_number,
          phone,
          bio,
          years_experience,
          specializations,
          profile_photo_url,
          is_currently_on_shift,
          status,
          shift_started_at,
          created_at,
          updated_at
        `)
        ?.single();

      if (error) {
        // Provide more specific error messages for common issues
        if (error?.code === '42501') {
          throw new Error('Permission denied. Please ensure you are logged in as an admin or authorized user.');
        } else if (error?.code === '23505') {
          throw new Error('License number already exists. Please use a unique license number.');
        } else if (error?.message?.includes('violates check constraint')) {
          throw new Error('Invalid data provided. Please check all fields and try again.');
        } else {
          throw new Error(`Failed to update agent: ${error?.message}`);
        }
      }

      if (!data) {
        throw new Error('No data returned after update. The agent may not exist or you may not have permission to update it.');
      }

      return { data, error: null };
    } catch (error) {
      console.error('BailAgentService updateAgent error:', error);
      return { 
        data: null, 
        error: error?.message || 'Failed to update bail agent'
      };
    }
  },

  // Set agent on shift (automatically sets others off shift)
  async setAgentOnShift(agentId) {
    try {
      if (!agentId) {
        throw new Error('Agent ID is required');
      }

      const { data, error } = await supabase?.rpc('set_agent_on_shift', { agent_uuid: agentId });

      if (error) {
        throw new Error(`Failed to set agent on shift: ${error?.message}`);
      }

      // Trigger a custom event to notify all components about the agent change
      window.dispatchEvent(new CustomEvent('bailAgentShiftChanged', { 
        detail: { agentId, timestamp: new Date().toISOString() }
      }));

      return { data, error: null };
    } catch (error) {
      return { 
        data: null, 
        error: error?.message || 'Failed to update agent shift status'
      };
    }
  },

  // Delete bail agent
  async deleteAgent(agentId) {
    try {
      if (!agentId) {
        throw new Error('Agent ID is required');
      }

      const { error } = await supabase?.from('bail_agents')?.delete()?.eq('id', agentId);

      if (error) {
        throw new Error(`Failed to delete agent: ${error?.message}`);
      }

      return { data: true, error: null };
    } catch (error) {
      return { 
        data: null, 
        error: error?.message || 'Failed to delete bail agent'
      };
    }
  },

  // Upload agent profile photo
  async uploadProfilePhoto(agentId, file) {
    try {
      if (!agentId || !file) {
        throw new Error('Agent ID and file are required');
      }

      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `${agentId}.${fileExt}`;
      const filePath = `profiles/${fileName}`;

      // Upload file to Supabase storage
      const { data: uploadData, error: uploadError } = await supabase?.storage?.from('agent-profiles')?.upload(filePath, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) {
        throw new Error(`Upload failed: ${uploadError?.message}`);
      }

      // Get public URL
      const { data: urlData } = supabase?.storage?.from('agent-profiles')?.getPublicUrl(filePath);

      const publicUrl = urlData?.publicUrl;

      // Update agent record with photo URL
      const { data: updateData, error: updateError } = await supabase?.from('bail_agents')?.update({ 
          profile_photo_url: publicUrl,
          updated_at: new Date()?.toISOString()
        })?.eq('id', agentId)?.select()?.single();

      if (updateError) {
        throw new Error(`Failed to update agent photo URL: ${updateError?.message}`);
      }

      return { data: { ...updateData, profile_photo_url: publicUrl }, error: null };
    } catch (error) {
      return { 
        data: null, 
        error: error?.message || 'Failed to upload profile photo'
      };
    }
  },

  // Remove agent profile photo
  async removeProfilePhoto(agentId) {
    try {
      if (!agentId) {
        throw new Error('Agent ID is required');
      }

      // Update agent record to remove photo URL
      const { data, error } = await supabase?.from('bail_agents')?.update({ 
          profile_photo_url: null,
          updated_at: new Date()?.toISOString()
        })?.eq('id', agentId)?.select()?.single();

      if (error) {
        throw new Error(`Failed to remove agent photo: ${error?.message}`);
      }

      return { data, error: null };
    } catch (error) {
      return { 
        data: null, 
        error: error?.message || 'Failed to remove profile photo'
      };
    }
  }
};